import { ZodObject, ZodSchema, z } from 'zod'
import I18N from '@leverege/i18n'

import { createLogs } from './Util.js'
import { FIELDS_SCHEMA, OBJECT_VALIDATOR_SCHEMA } from './Schemas.js'

const { t } = I18N.ns( 'object-validator.ObjectValidator' )
const module = 'ObjectValidator'

export default class ObjectValidator {
  #throwOnError;
  #fieldsValidator;
  #objectValidator;
  #log = {
    ...createLogs( module )
  };

  /**
   * @typedef {z.infer<typeof FIELDS_SCHEMA> Fields}
   */

  /**
   * @typedef {z.infer<typeof OBJECT_VALIDATOR_SCHEMA> ObjectValidator}
   */

  /**
   * 
   * @param {object} options The validator options
   * @param {Fields} options.fields Definition of object fields, each with name and optional labelDecorator and valueValidator
   * @param {ObjectValidator} options.objectValidators One or more object validators to apply to the entire object when validating
   * @param {boolean} options.throwOnError Whether or not to throw an error if validation fails. If set to false, errors will be returned alongside results.
   */
  constructor( options ) {
    const { fields = {}, objectValidators, throwOnError = false } = options || {}

    this.fields = FIELDS_SCHEMA.parse( fields )
    this.objectValidators = OBJECT_VALIDATOR_SCHEMA.optional().parse( objectValidators )
    this.dirtyFields = {}
    this.#throwOnError = throwOnError
  }

  /**
   * @returns {import('zod').ZodSchema}
   */
  get fieldsValidator() {
    if ( this.#fieldsValidator ) {
      return this.#fieldsValidator
    }

    const validator = {}

    Object
      .entries( this.fields )
      .forEach( ( [ key, value ] ) => {
        if ( value.valueValidator ) {
          if ( Array.isArray( value.valueValidator ) ) {
            validator[key] = value.valueValidator.reduce( ( prev, curr ) => {
              return prev.and( curr )
            }, z.any() )
          } else {
            validator[key] = value.valueValidator
          }
        } else {
          validator[key] = z.any()
        }
      } )

    this.#fieldsValidator = Object.keys( validator ).length > 0 ? z.object( validator ) : z.object( {} )
    
    return this.#fieldsValidator
  }

  /**
   * @returns {import('zod').ZodSchema}
   */
  get objectValidator() {
    if ( this.#objectValidator ) {
      return this.#objectValidator
    }

    let validator

    if ( this.objectValidators && Array.isArray( this.objectValidators ) && this.objectValidators.length > 0 ) {
      if ( Array.isArray( this.objectValidators ) ) {
        validator = this.objectValidators.reduce( ( prev, curr ) => {
          if ( !prev ) {
            return curr
          }
          if ( curr instanceof ZodObject ) {
            return prev.merge( curr )
          }

          return prev.and( curr )
        } )
      } else {
        validator = this.objectValidators
      }
    } else if ( this.objectValidators instanceof ZodSchema ) {
      validator = this.objectValidators
    } else {
      validator = z.object( {} )
    }

    this.#objectValidator = validator?.passthrough ? validator.passthrough() : validator

    return this.#objectValidator
  }

  getValueValidator( all = false ) {
    return all ? this.fieldsValidator.passthrough() : this.fieldsValidator.pick( this.dirtyFields ).passthrough()
  }

  errorMap = ( issue, ctx ) => {
    const [ path, ...rest ] = issue.path
    let name = this.fields[path]?.name || path
    if ( rest.length > 0 ) {
      name = rest.reduce( ( prev, curr ) => {
        const index = parseInt( curr )

        if ( !Number.isNaN( index ) ) {
          return `${prev} item ${index + 1}`
        }

        return `${prev}/${curr}`
      }, name )
    }

    // TODO: I18Nize
    switch ( issue.code ) {
      case z.ZodIssueCode.custom:
        return { message : t( 'generalInvalid', { name } ) }
      case z.ZodIssueCode.too_big: {
        if ( issue.type === 'string' ) {
          if ( issue.inclusive ) {
            return { message : t( 'tooBigStringInclusive', { name, max : issue.maximum } ) }
          }
          return { message : t( 'tooBigStringExclusive', { name, max : issue.maximum } ) }
        }

        if ( issue.inclusive ) {
          return { message : t( 'tooBigNumberInclusive', { name, max : issue.maximum } ) }  
        }
        return { message : t( 'tooBigNumberExclusive', { name, max : issue.maximum } ) }
      }
      case z.ZodIssueCode.too_small: {
        if ( issue.type === 'string' ) {
          if ( issue.inclusive ) {
            return { message : t( 'tooSmallStringInclusive', { name, min : issue.minimum } ) }
          }
          return { message : t( 'tooSmallStringExclusive', { name, min : issue.minimum } ) }
        }

        if ( issue.inclusive ) {
          return { message : t( 'tooSmallNumberInclusive', { name, min : issue.minimum } ) }  
        }
        return { message : t( 'tooSmallNumberExclusive', { name, min : issue.minimum } ) }
      }
      case z.ZodIssueCode.invalid_enum_value:
        return { message : t( 'enumInvalid', { name, options : issue.options.join( ',' ) } ) }
      case z.ZodIssueCode.invalid_date:
        return { message : t( 'dateInvalid', { name } ) }
      case z.ZodIssueCode.invalid_string: {
        const { type, validation } = issue
        return { message : ( type || validation ) ? t( 'stringInvalid', { name, type : type || validation } ) : t( 'generalInvalid', { name } ) }
      }
      case z.ZodIssueCode.invalid_type: {
        const { expected, received } = issue
        return { message : t( 'typeInvalid', { name, received, expected } ) }
      }
      default: {
        return { message : ctx.defaultError }
      }
    }
  }

  decorateLabel( name, value ) {
    const decorator = this.fields[name]?.labelDecorator
    if ( decorator ) {
      if ( Array.isArray( decorator ) ) {
        return decorator.reduce( ( prev, curr ) => {
          return curr( prev )
        }, value )
      } 
      return decorator( value )
    }
    
    return value
  }

  /**
   * @typedef {object} ValidationResult
   * @property {object} result - The form data as parsed by the validator
   * @property {import('zod').ZodError | undefined} error - A ZodError respresenting all validation errors
   */

  /**
   * 
   * @returns {ValidationResult}
   */
  async validate( { data : changedKey, value : formState }, allFields = false ) {
    let error
    let result

    if ( typeof changedKey === 'string' ) {
      this.dirtyFields[changedKey] = true
    } else if ( changedKey && typeof changedKey === 'object' && !Array.isArray( changedKey ) ) {
      Object
        .keys( changedKey )
        .forEach( ( key ) => {
          this.dirtyFields[key] = true
        } )
    }

    try {
      result = await this.getValueValidator( allFields ).parseAsync( formState, {
        errorMap : this.errorMap
      } )
    } catch ( err ) {
      if ( this.#throwOnError ) {
        throw err
      }
      error = err
    }

    try {
      result = await this.objectValidator.parseAsync( formState, {
        errorMap : this.errorMap
      } )
    } catch ( err ) {
      if ( this.#throwOnError ) {
        throw err
      }
      
      if ( error ) {
        error.addIssues( err.issues )
      } else {
        error = err
      }
    }

    return {
      error,
      result : result || formState
    }
  }
}
